/**
 * O desenho do resultado — as quatro formas que os relatórios têm.
 *
 * **A tabela corta em 200 e diz quanto escondeu.** Um relatório traz de 2 a 32
 * mil linhas; desenhar tudo no DOM trava a rolagem sem ajudar ninguém, porque
 * ninguém percorre 32 mil linhas com o olho — procura. O campo de filtro é em
 * memória e instantâneo, e o CSV leva o resultado inteiro, não só o desenhado.
 *
 * **O gráfico só aparece onde o sistema antigo tem um.** Onde o legado não tem
 * gráfico, não invento.
 *
 * **São duas leituras diferentes, e trocá-las estraga o relatório.** O `top`
 * responde "quem é o maior": soma por rótulo, ordena por valor, corta nos 15 —
 * uma barra por linha em 2.581 linhas não é gráfico, é ruído. O `tempo`
 * responde "está crescendo?", e para isso cada ponto tem de cair na posição
 * real dele no calendário: a série do crescimento tem 2018-03, 2021-02 e
 * 2021-05, e desenhá-los lado a lado diria que o consumo caiu de um mês para o
 * outro, quando foram três anos sem nenhum acesso.
 */

import { useMemo, useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import { ErroDaApi } from "@/api/erros"
import type { Coluna, Relatorio, Saida } from "./catalogo"

const DESENHADAS = 150
const NO_GRAFICO = 15

type Linha = Record<string, unknown>

export function Resultado({ relatorio, resposta }: {
  relatorio: Relatorio
  resposta: Record<string, unknown>
}) {
  const s = relatorio.saida

  if (s.forma === "tiles") return <Tiles campos={s.campos} dados={resposta} />

  // Os gráficos vêm antes das tabelas em qualquer forma de saída: cada um diz
  // de qual array da resposta se alimenta, então não precisa morar dentro do
  // ramo que desenha aquele array.
  const graficos = <Graficos relatorio={relatorio} resposta={resposta} />

  if (s.forma === "series") {
    return <ResultadoSeries relatorio={relatorio} s={s} resposta={resposta} graficos={graficos} />
  }

  if (s.forma === "composta") {
    return <ResultadoComposta relatorio={relatorio} s={s} resposta={resposta} graficos={graficos} />
  }

  // tabela simples
  const linhas = arrayEm(resposta, s.de ?? "")
  return (
    <div style={{ padding: 14 }}>
      {graficos}
      <Bloco rotulo="" linhas={linhas} colunas={s.colunas} detalhe={relatorio.detalhe} />
    </div>
  )
}

/** Cada gráfico declarado, alimentado pelo array que ele aponta. */
function Graficos({ relatorio, resposta }: {
  relatorio: Relatorio
  resposta: Record<string, unknown>
}) {
  if (!relatorio.graficos?.length) return null
  return (
    <>
      {relatorio.graficos.map((g, i) => {
        const linhas = arrayEm(resposta, g.de ?? "")
        return g.tipo === "tempo"
          ? <SerieNoTempo key={i} linhas={linhas} valor={g.valor} titulo={g.titulo}
                          ano={g.ano ?? "ano"} mes={g.mes ?? "mes"} />
          : <Grafico key={i} linhas={linhas} rotulo={g.rotulo} valor={g.valor} titulo={g.titulo} />
      })}
    </>
  )
}

/**
 * O cabeçalho que vem da própria resposta — e o aviso que depende dela.
 *
 * Existe porque há número que não se lê sozinho. O consumo da DiagIA é o caso:
 * a mesma soma vale como custo do mês quando 92% das perguntas foram medidas e
 * não vale nada quando foram 4%. O aviso aparece **antes** das tabelas, e não
 * como nota de rodapé, porque a nota de rodapé ninguém lê.
 */
function Painel({ relatorio, resposta }: {
  relatorio: Relatorio
  resposta: Record<string, unknown>
}) {
  const p = relatorio.painel
  if (!p) return null

  const alerta = p.alerta ? emCaminho(resposta, p.alerta.chave) === false : false

  return (
    <div style={{ marginBottom: 14 }}>
      {alerta && (
        <div style={{
          padding: "9px 12px", borderRadius: 6, marginBottom: 8, fontSize: 12, lineHeight: 1.55,
          background: "var(--aviso-soft)", color: "var(--aviso-on-soft)",
          border: "1px solid var(--aviso)", maxWidth: "88ch",
        }}>
          <b>Cuidado com este número.</b> {p.alerta!.texto}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {p.campos.map((c) => {
          const v = emCaminho(resposta, c.chave)
          return (
            <div key={c.chave} style={{
              padding: "7px 12px", borderRadius: 6, border: "1px solid var(--line2)", minWidth: 108,
            }}>
              <div style={{ fontSize: 10, color: "var(--txt3)" }}>{c.rotulo}</div>
              <div style={{ fontSize: 17, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                {typeof v === "number" ? v.toLocaleString("pt-BR") : String(v ?? "—")}
                {c.sufixo ?? ""}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Lê um valor de dentro da resposta por caminho com ponto. */
function emCaminho(objeto: Record<string, unknown>, caminho: string): unknown {
  let atual: unknown = objeto
  for (const parte of caminho.split(".")) {
    if (atual == null || typeof atual !== "object") return undefined
    atual = (atual as Record<string, unknown>)[parte]
  }
  return atual
}

/** Pega um array de dentro da resposta, aceitando caminho com ponto. */
function arrayEm(objeto: Record<string, unknown>, caminho: string): Linha[] {
  if (!caminho) {
    const direto = objeto[""]
    if (Array.isArray(direto)) return direto as Linha[]
  }
  let atual: unknown = objeto
  for (const parte of caminho.split(".")) {
    if (!parte) continue
    if (atual && typeof atual === "object") atual = (atual as Record<string, unknown>)[parte]
    else return []
  }
  return Array.isArray(atual) ? (atual as Linha[]) : []
}

/** Números soltos no topo da resposta — `qtdAchou`, `totalLinhas`, `indicadores`. */
function Indicadores({ resposta }: { resposta: Record<string, unknown> }) {
  const numeros = Object.entries(resposta)
    .filter(([, v]) => typeof v === "number")
    .concat(Object.entries((resposta.indicadores as Record<string, unknown>) ?? {})
      .filter(([, v]) => typeof v === "number"))

  if (numeros.length === 0) return null

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
      {numeros.map(([k, v]) => (
        <div key={k} style={{
          padding: "7px 12px", borderRadius: 6, border: "1px solid var(--line2)", minWidth: 108,
        }}>
          <div style={{ fontSize: 10, color: "var(--txt3)" }}>{humano(k)}</div>
          <div style={{ fontSize: 17, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {(v as number).toLocaleString("pt-BR")}
          </div>
        </div>
      ))}
    </div>
  )
}

function Tiles({ campos, dados }: {
  campos: { chave: string; rotulo: string }[]
  dados: Record<string, unknown>
}) {
  return (
    <div style={{
      padding: 16, display: "grid", gap: 10,
      gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))",
    }}>
      {campos.map((c) => (
        <div key={c.chave} style={{
          padding: "12px 14px", borderRadius: 7, border: "1px solid var(--line2)",
        }}>
          <div style={{ fontSize: 10.5, color: "var(--txt3)" }}>{c.rotulo}</div>
          <div style={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {typeof dados[c.chave] === "number"
              ? (dados[c.chave] as number).toLocaleString("pt-BR")
              : "—"}
          </div>
        </div>
      ))}
    </div>
  )
}

function Bloco({ rotulo, linhas, colunas, detalhe }: {
  rotulo: string
  linhas: Linha[]
  colunas: Coluna[]
  detalhe?: Relatorio["detalhe"]
}) {
  const [busca, setBusca] = useState("")
  const [aberta, setAberta] = useState<string | number | null>(null)
  const [pagina, setPagina] = useState(1)

  const achadas = useMemo(() => {
    const t = busca.trim().toLowerCase()
    if (!t) return linhas
    return linhas.filter((l) => colunas.some((c) => String(l[c.chave] ?? "").toLowerCase().includes(t)))
  }, [linhas, colunas, busca])

  // Volta para a página 1 ao alterar o filtro ou receber novas linhas
  useEffect(() => { setPagina(1) }, [busca, linhas])

  const totalPaginas = Math.ceil(achadas.length / DESENHADAS) || 1
  const paginaValida = Math.min(Math.max(1, pagina), totalPaginas)
  const desenhadas = achadas.slice((paginaValida - 1) * DESENHADAS, paginaValida * DESENHADAS)

  return (
    <section style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
        {rotulo && (
          <h3 style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>{rotulo}</h3>
        )}
        <span style={{ fontSize: 11, color: "var(--txt2)" }}>
          {achadas.length.toLocaleString("pt-BR")} linha{achadas.length === 1 ? "" : "s"}
        </span>
        {linhas.length > 20 && (
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="filtrar…"
            style={{
              marginLeft: "auto", padding: "3px 8px", fontSize: 11, width: 190,
              border: "1px solid var(--line)", borderRadius: 5,
              background: "var(--surface)", color: "var(--txt)",
            }}
          />
        )}
      </div>

      {linhas.length === 0 ? (
        <p style={{ fontSize: 12, color: "var(--txt3)" }}>
          Nada no período escolhido.
        </p>
      ) : (
        <div style={{ border: "1px solid var(--line2)", borderRadius: 6 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
            <thead>
              <tr>
                {colunas.map((c) => (
                  <th key={c.chave} style={{
                    position: "sticky", top: 0, zIndex: 10, background: "var(--surface)",
                    textAlign: c.numero ? "right" : "left", padding: "5px 8px",
                    fontSize: 10.5, fontWeight: 600, color: "var(--txt2)",
                    borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
                    width: c.largura,
                  }}>
                    {c.rotulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {desenhadas.map((l, i) => {
                const id = detalhe ? (l[detalhe.campoId] as string | number | undefined) : undefined
                const podeAbrir = detalhe != null && id != null && id !== ""
                const abertaAqui = podeAbrir && aberta === id

                return (
                  <Fragmento key={i}>
                    <tr
                      onClick={podeAbrir ? () => setAberta(abertaAqui ? null : id!) : undefined}
                      style={{
                        borderBottom: "1px solid var(--line2)",
                        cursor: podeAbrir ? "pointer" : undefined,
                        background: abertaAqui ? "var(--surface2)" : undefined,
                      }}
                    >
                      {colunas.map((c) => (
                        <td key={c.chave} style={{
                          padding: "4px 8px",
                          textAlign: c.numero ? "right" : "left",
                          fontVariantNumeric: c.numero ? "tabular-nums" : undefined,
                          whiteSpace: c.numero ? "nowrap" : undefined,
                          maxWidth: c.largura ?? 320,
                          overflow: "hidden", textOverflow: "ellipsis",
                        }} title={texto(l[c.chave])}>
                          {celula(l[c.chave], c)}
                        </td>
                      ))}
                    </tr>
                    {abertaAqui && (
                      <tr>
                        <td colSpan={colunas.length} style={{ padding: 0 }}>
                          <Detalhe descricao={detalhe!} id={id!} />
                        </td>
                      </tr>
                    )}
                  </Fragmento>
                )
              })}
            </tbody>
          </table>
          {totalPaginas > 1 && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, padding: "7px 14px",
              borderTop: "1px solid var(--line2)", background: "var(--surface)",
            }}>
              <button
                disabled={paginaValida <= 1}
                onClick={() => setPagina(paginaValida - 1)}
                style={{
                  border: "1px solid var(--line)", borderRadius: 6, padding: "4px 10px", fontSize: 11,
                  cursor: paginaValida <= 1 ? "default" : "pointer",
                  background: "var(--surface)", color: paginaValida <= 1 ? "var(--txt3)" : "var(--txt)",
                }}
              >
                anterior
              </button>
              <span style={{ fontSize: 11, color: "var(--txt2)" }}>
                página {paginaValida} de {totalPaginas}
              </span>
              <button
                disabled={paginaValida >= totalPaginas}
                onClick={() => setPagina(paginaValida + 1)}
                style={{
                  border: "1px solid var(--line)", borderRadius: 6, padding: "4px 10px", fontSize: 11,
                  cursor: paginaValida >= totalPaginas ? "default" : "pointer",
                  background: "var(--surface)", color: paginaValida >= totalPaginas ? "var(--txt3)" : "var(--txt)",
                }}
              >
                próxima
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

/**
 * O detalhe de uma linha.
 *
 * Genérico de propósito: o catálogo diz a rota, o campo que vira `{id}` e o
 * título, e o resto é desenhar o que voltou. Dois relatórios usam — a busca sem
 * consulta e o log da DiagIA —, e são bem diferentes um do outro; um desenho
 * específico para cada seria dois desenhos para manter.
 */
function Detalhe({ descricao, id }: {
  descricao: NonNullable<Relatorio["detalhe"]>
  id: string | number
}) {
  const rota = descricao.rota.replace("{id}", encodeURIComponent(String(id)))

  const consulta = useQuery({
    queryKey: ["relatorio-detalhe", rota] as const,
    staleTime: 5 * 60_000,
    queryFn: () => obter<Record<string, unknown>>(rota, {}),
  })

  return (
    <div style={{
      padding: "10px 14px", background: "var(--surface2)",
      borderBottom: "1px solid var(--line2)",
    }}>
      <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600 }}>{descricao.titulo}</p>

      {consulta.isPending ? (
        <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt3)" }}>Abrindo…</p>
      ) : consulta.error ? (
        <p style={{ margin: 0, fontSize: 11.5, color: "var(--erro)" }}>
          {consulta.error instanceof ErroDaApi ? consulta.error.message : "Não foi possível abrir."}
        </p>
      ) : (
        <>
          <Campos dado={consulta.data ?? {}} />
          {descricao.nota && (
            <p style={{ margin: "8px 0 0", fontSize: 10, color: "var(--txt3)", lineHeight: 1.5 }}>
              {descricao.nota}
            </p>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Quanto de um campo cabe no painel.
 *
 * O detalhe do log da DiagIA traz a conversa inteira dentro de `mensagens`, e
 * despejá-la como JSON num painel de relatório é ilegível — para ler conversa
 * existe a tela de Conversas da DiagIA. Aqui o campo é cortado dizendo o
 * tamanho, em vez de empurrar quinze mil caracteres para dentro da tabela.
 */
const CABE_NO_CAMPO = 300

/** Os campos que vieram, em pares rótulo/valor. Listas e objetos viram texto. */
function Campos({ dado }: { dado: Record<string, unknown> }) {
  const pares = Object.entries(dado).filter(([, v]) => v !== null && v !== "")

  if (pares.length === 0) {
    return <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt3)" }}>Sem detalhe gravado.</p>
  }

  return (
    <div style={{
      display: "grid", gap: "4px 14px",
      gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    }}>
      {pares.map(([k, v]) => {
        // Como TEXTO: parte deste conteudo e o que um cliente digitou.
        const bruto = typeof v === "object" ? JSON.stringify(v) : String(v)
        const grande = bruto.length > CABE_NO_CAMPO

        return (
          <div key={k} style={{ fontSize: 11, lineHeight: 1.5 }}>
            <span style={{ color: "var(--txt3)" }}>{humano(k)}: </span>
            <span style={{ wordBreak: "break-word" }}>
              {grande ? bruto.slice(0, CABE_NO_CAMPO) : bruto}
            </span>
            {grande && (
              <span style={{ color: "var(--txt3)" }}>
                {" "}… ({bruto.length.toLocaleString("pt-BR")} caracteres)
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** O fragmento curto nao aceita `key`; dentro do `tbody` a linha extra precisa de um. */
function Fragmento({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

/**
 * Um gráfico de barras do top 15, em HTML puro.
 *
 * Não vale carregar biblioteca de gráfico para isto: é uma barra proporcional
 * ao maior valor, e a leitura que interessa — quem está no topo, e por quanto —
 * cabe numa div com largura em porcentagem.
 */
function Grafico({ linhas, rotulo, valor, titulo }: {
  linhas: Linha[]
  rotulo: string
  valor: string
  titulo?: string
}) {
  /**
   * Soma os valores que caem no mesmo rótulo antes de ordenar.
   *
   * Sem isso o gráfico mente: em "acesso ao sistema" as linhas são pares
   * assunto × sistema, então "Injeção eletrônica" aparecia duas vezes, com
   * 17.532 e 638, como se fossem sistemas diferentes. A pergunta que o gráfico
   * responde é "qual sistema é mais acessado" — e a resposta é a soma.
   */
  const topo = useMemo(() => {
    const somado = new Map<string, number>()
    for (const l of linhas) {
      const v = l[valor]
      if (typeof v !== "number") continue
      const nome = texto(l[rotulo])
      somado.set(nome, (somado.get(nome) ?? 0) + v)
    }
    return [...somado.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, NO_GRAFICO)
  }, [linhas, rotulo, valor])

  if (topo.length < 2) return null
  const maior = topo[0]![1]
  if (!maior) return null

  return (
    <section style={{ marginBottom: 20 }}>
      <h3 style={{ margin: "0 0 7px", fontSize: 12, fontWeight: 600 }}>
        {titulo ? `${titulo} — os ${topo.length} maiores` : `Os ${topo.length} maiores`}
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {topo.map(([nome, v]) => {
          return (
            <div key={nome} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                flex: "0 0 240px", width: 240, fontSize: 11, color: "var(--txt2)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }} title={nome}>
                {nome || "(sem nome)"}
              </span>
              <div style={{ flex: 1, minWidth: 0, height: 13, background: "var(--surface2)", borderRadius: 3 }}>
                <div style={{
                  width: `${Math.max(1, (v / maior) * 100)}%`, height: "100%",
                  background: "var(--acao)", borderRadius: 3,
                }} />
              </div>
              <span style={{
                flex: "0 0 88px", textAlign: "right", fontSize: 11,
                fontVariantNumeric: "tabular-nums",
              }}>
                {v.toLocaleString("pt-BR")}
              </span>
            </div>
          )
        })}
      </div>
    </section>
  )
}

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun",
               "jul", "ago", "set", "out", "nov", "dez"]

/**
 * Uma série mensal no eixo do tempo de verdade.
 *
 * **O eixo é o calendário, não a ordem das linhas.** Cada coluna fica na
 * posição do seu mês entre o primeiro e o último da série, então um buraco de
 * três anos aparece como três anos de buraco. É a diferença entre "o consumo
 * caiu" e "ninguém acessou nesse período" — e a série do crescimento tem
 * exatamente esse caso: 2018-03, 2021-02, 2021-05.
 *
 * **Mês ausente é zero, e isso é honesto**: o relatório conta acessos, e mês
 * sem linha é mês sem acesso. Por isso a coluna simplesmente não é desenhada,
 * em vez de o traço ser interpolado por cima do vazio.
 *
 * HTML puro como o outro gráfico: posição e altura em porcentagem resolvem, e
 * não vale carregar biblioteca para isto.
 */
function SerieNoTempo({ linhas, valor, titulo, ano, mes }: {
  linhas: Linha[]
  valor: string
  titulo?: string
  ano: string
  mes: string
}) {
  const serie = useMemo(() => {
    const pontos: { t: number; v: number; rotulo: string }[] = []
    for (const l of linhas) {
      const v = l[valor]
      const a = Number(l[ano])
      const m = Number(l[mes])
      if (typeof v !== "number" || !Number.isFinite(a) || !Number.isFinite(m)) continue
      pontos.push({ t: a * 12 + (m - 1), v, rotulo: `${MESES[m - 1] ?? m}/${a}` })
    }
    pontos.sort((x, y) => x.t - y.t)
    return pontos
  }, [linhas, valor, ano, mes])

  if (serie.length < 2) return null

  const inicio = serie[0]!.t
  const fim = serie[serie.length - 1]!.t
  const meses = fim - inicio + 1
  const maior = Math.max(...serie.map((p) => p.v))
  if (!maior) return null

  // Uma marca por ano, no janeiro de cada um -- 90 rótulos mensais não cabem.
  const anos: { t: number; ano: number }[] = []
  for (let t = Math.ceil(inicio / 12) * 12; t <= fim; t += 12) {
    anos.push({ t, ano: Math.floor(t / 12) })
  }

  const emPorcento = (t: number) => ((t - inicio) / meses) * 100
  const largura = Math.max(0.5, (1 / meses) * 100)

  return (
    <section style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 5 }}>
        <h3 style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>{titulo ?? "No tempo"}</h3>
        <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
          {serie.length} {serie.length === 1 ? "mês" : "meses"} com acesso, de{" "}
          {serie[0]!.rotulo} a {serie[serie.length - 1]!.rotulo} · maior:{" "}
          <span className="num">{maior.toLocaleString("pt-BR")}</span>
        </span>
      </div>

      <div style={{
        position: "relative", height: 118, borderBottom: "1px solid var(--line)",
        background: "var(--surface2)", borderRadius: "3px 3px 0 0",
      }}>
        {/* a marca do ano atravessa o fundo, para dar escala ao buraco */}
        {anos.map((a) => (
          <div key={a.t} style={{
            position: "absolute", left: `${emPorcento(a.t)}%`, top: 0, bottom: 0,
            width: 1, background: "var(--line2)",
          }} />
        ))}
        {serie.map((p) => (
          <div
            key={p.t}
            className="grafico-barra-hover"
            style={{
              position: "absolute", left: `${emPorcento(p.t)}%`, width: `${largura}%`,
              minWidth: 2, bottom: 0, height: `${Math.max(2, (p.v / maior) * 100)}%`,
              background: "var(--acao)", borderRadius: "2px 2px 0 0",
            }}
          >
            <div className="tooltip-barra">{p.rotulo}: {p.v.toLocaleString("pt-BR")}</div>
          </div>
        ))}
      </div>

      <div style={{ position: "relative", height: 14 }}>
        {anos.map((a) => (
          <span key={a.t} style={{
            position: "absolute", left: `${emPorcento(a.t)}%`, top: 2,
            fontSize: 9.5, color: "var(--txt3)", transform: "translateX(-50%)",
            fontVariantNumeric: "tabular-nums",
          }}>
            {a.ano}
          </span>
        ))}
      </div>
    </section>
  )
}

function celula(v: unknown, c: Coluna): string {
  if (v === null || v === undefined || v === "") return c.numero ? "0" : "—"
  if (typeof v === "number") {
    return c.numero && !c.ano ? v.toLocaleString("pt-BR") : String(v)
  }
  if (typeof v === "boolean") return v ? "sim" : "não"
  const t = String(v)
  // Datas ISO ficam ilegíveis cruas: "2026-08-01T14:22:03" vira "01/08/2026 14:22".
  const data = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(t)
  if (data) return `${data[3]}/${data[2]}/${data[1]} ${data[4]}:${data[5]}`
  return t
}

function texto(v: unknown): string {
  return v === null || v === undefined ? "" : String(v)
}

/** `qtdNaoAchou` → "Qtd nao achou". Serve para os campos que vêm soltos. */
function humano(chave: string): string {
  const s = chave.replace(/([A-Z])/g, " $1").toLowerCase().trim()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export type { Saida }

/**
 * Renderiza relatórios que possuem várias "séries" (ex: por dia, por mês).
 * Em vez de exibir todos os blocos verticalmente, cria um switch para a pessoa alternar.
 */
function ResultadoSeries({ relatorio, s, resposta, graficos }: {
  relatorio: Relatorio
  s: Extract<Saida, { forma: "series" }>
  resposta: Record<string, unknown>
  graficos: React.ReactNode
}) {
  const [ativa, setAtiva] = useState(s.series[0]?.chave ?? "")

  // Se trocar de relatório, garante que escolhe uma série que existe no novo
  useEffect(() => {
    if (!s.series.some((sr) => sr.chave === ativa)) {
      setAtiva(s.series[0]?.chave ?? "")
    }
  }, [s, ativa])

  const serie = s.series.find((sr) => sr.chave === ativa) || s.series[0]

  return (
    <div style={{ padding: 14 }}>
      {graficos}

      {s.series.length > 1 && (
        <div style={{
          display: "flex", gap: 6, marginBottom: 16, background: "var(--surface2)",
          padding: 4, borderRadius: 8, width: "fit-content",
        }}>
          {s.series.map((sr) => {
            const isAtiva = ativa === sr.chave
            return (
              <button
                key={sr.chave}
                onClick={() => setAtiva(sr.chave)}
                style={{
                  padding: "6px 14px", fontSize: 12, borderRadius: 6, cursor: "pointer",
                  background: isAtiva ? "var(--surface)" : "transparent",
                  color: isAtiva ? "var(--txt)" : "var(--txt3)",
                  boxShadow: isAtiva ? "var(--sombra)" : "none",
                  fontWeight: isAtiva ? 600 : 400,
                  transition: "all 0.15s",
                }}
              >
                {sr.rotulo}
              </button>
            )
          })}
        </div>
      )}

      {serie && (
        <Bloco
          key={serie.chave}
          rotulo="" // Limpo porque o botão de aba já descreve a visão
          linhas={arrayEm(resposta, serie.chave)}
          colunas={serie.colunas}
          detalhe={relatorio.detalhe}
        />
      )}
    </div>
  )
}

/**
 * Renderiza relatórios que possuem vários "blocos" (forma composta).
 * Em vez de exibir todos os blocos verticalmente, cria um switch para a pessoa alternar.
 */
function ResultadoComposta({ relatorio, s, resposta, graficos }: {
  relatorio: Relatorio
  s: Extract<Saida, { forma: "composta" }>
  resposta: Record<string, unknown>
  graficos: React.ReactNode
}) {
  const [ativa, setAtiva] = useState(s.blocos[0]?.de ?? "")

  // Se trocar de relatório, garante que escolhe um bloco que existe no novo
  useEffect(() => {
    if (!s.blocos.some((b) => b.de === ativa)) {
      setAtiva(s.blocos[0]?.de ?? "")
    }
  }, [s, ativa])

  const bloco = s.blocos.find((b) => b.de === ativa) || s.blocos[0]

  return (
    <div style={{ padding: 14 }}>
      <Painel relatorio={relatorio} resposta={resposta} />
      {!relatorio.painel && <Indicadores resposta={resposta} />}
      {graficos}

      {s.blocos.length > 1 && (
        <div style={{
          display: "flex", gap: 6, marginBottom: 16, background: "var(--surface2)",
          padding: 4, borderRadius: 8, width: "fit-content",
        }}>
          {s.blocos.map((b) => {
            const isAtiva = ativa === b.de
            return (
              <button
                key={b.de}
                onClick={() => setAtiva(b.de)}
                style={{
                  padding: "6px 14px", fontSize: 12, borderRadius: 6, cursor: "pointer",
                  background: isAtiva ? "var(--surface)" : "transparent",
                  color: isAtiva ? "var(--txt)" : "var(--txt3)",
                  boxShadow: isAtiva ? "var(--sombra)" : "none",
                  fontWeight: isAtiva ? 600 : 400,
                  transition: "all 0.15s",
                }}
              >
                {b.rotulo}
              </button>
            )
          })}
        </div>
      )}

      {bloco && (
        <Bloco
          key={bloco.de}
          rotulo="" // Limpo porque o botão de aba já descreve a visão
          linhas={arrayEm(resposta, bloco.de)}
          colunas={bloco.colunas}
          detalhe={relatorio.detalhe}
        />
      )}
    </div>
  )
}
